import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import ActionPathField from './ActionPathField';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { appendFileNameToFolderPath, normalizePathValue } from '../../Utils/pathUtils';

const cardTitleTypographyProps = {
  sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 },
};

const CopyFilePathCard = ({
  browseDisabled,
  browseLabel,
  caseid,
  error,
  helperText,
  label,
  onBrowse,
  onChange,
  placeholder,
  tags,
  title,
  value,
  children,
}) => (
  <Grid item xs={12}>
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={title}
        titleTypographyProps={cardTitleTypographyProps}
        sx={{ p: { xs: 1.5, sm: 2 } }}
      />
      <CardContent>
        <ActionPathField
          browseDisabled={browseDisabled}
          browseLabel={browseLabel}
          caseid={caseid}
          error={error}
          helperText={helperText}
          label={label}
          onBrowse={onBrowse}
          onChange={onChange}
          placeholder={placeholder}
          tags={tags}
          value={value}
        />
        {children && <Box sx={{ mt: 2 }}>{children}</Box>}
      </CardContent>
    </Card>
  </Grid>
);

const ActionsCopyFile = ({ caseid, tags, settingId, onSaveStatusChange, initialScope = 'event' }) => {
  const { t } = useTranslation();
  const [targetPath, setTargetPath] = useState('');
  const [sourcePath, setSourcePath] = useState('');
  const [sourceBrowsingDisabled, setSourceBrowsingDisabled] = useState(false);
  const [targetBrowsingDisabled, setTargetBrowsingDisabled] = useState(false);
  const [replaceIfExists, setReplaceIfExists] = useState(false);
  const [replaceWarningOpen, setReplaceWarningOpen] = useState(false);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const {
    handleBrowseDirectory: handleBrowseTargetDirectory,
    FolderBrowserComponent: TargetFolderBrowserComponent,
  } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        setTargetPath(appendFileNameToFolderPath(folderPath, '{filename}.{file_extension}'));
      }
      setTargetBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowsingDisabled(false);
    },
  });

  const {
    handleBrowseDirectory: handleBrowseSourceFile,
    FolderBrowserComponent: SourceFileBrowserComponent,
  } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (filePath) => {
      if (filePath) {
        setSourcePath(filePath);
      }
      setSourceBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowsingDisabled(false);
    },
  });

  const customDataTags = useMemo(() => ([
    {
      key: 'tag_fileContentToWrite',
      label: t('workflow.copy_file_existing_content_tag', 'Existing target file content'),
      value: 'fileContentToWrite',
      description: t(
        'workflow.copy_file_existing_content_tag_help',
        'Content of the target file when it already exists',
      ),
    },
  ]), [t]);

  const availableTags = useMemo(
    () => (Array.isArray(tags) ? [...tags, ...customDataTags] : customDataTags),
    [customDataTags, tags],
  );

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'copyfile',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial = {}) => (
      normalizePathValue(targetPath) === normalizePathValue(initial.target)
      && normalizePathValue(sourcePath) === normalizePathValue(initial.sourceFilePath)
      && replaceIfExists === Boolean(initial.replaceIfExists)
    ),
    customSaveData: () => ({
      target: normalizePathValue(targetPath),
      sourceFilePath: normalizePathValue(sourcePath),
      replaceIfExists,
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setTargetPath(customData.target || '');
      setSourcePath(customData.sourceFilePath || '');
      setReplaceIfExists(Boolean(customData.replaceIfExists));
    },
  });

  const targetPathMissing = !targetPath.trim();
  const baseHandleSave = actionSettings.handleSave;

  const handleBrowseSourceClick = useCallback(() => {
    setSourceBrowsingDisabled(true);
    handleBrowseSourceFile();
  }, [handleBrowseSourceFile]);

  const handleBrowseTargetClick = useCallback(() => {
    setTargetBrowsingDisabled(true);
    handleBrowseTargetDirectory();
  }, [handleBrowseTargetDirectory]);

  const handleSave = useCallback(() => {
    if (targetPathMissing) {
      return;
    }
    if (replaceIfExists) {
      setReplaceWarningOpen(true);
      return;
    }
    baseHandleSave();
  }, [baseHandleSave, replaceIfExists, targetPathMissing]);

  const handleConfirmReplaceSave = useCallback(() => {
    setReplaceWarningOpen(false);
    baseHandleSave();
  }, [baseHandleSave]);

  return (
    <>
      <ActionSettingsLayout
        title={t('copyfile', 'Copy File')}
        isEnabled={actionSettings.isEnabled}
        onEnabledChange={actionSettings.setIsEnabled}
        isSaveDisabled={actionSettings.isSaveDisabled}
        hasValidationError={targetPathMissing}
        onSave={handleSave}
        onForceRun={actionSettings.handleForceRun}
        forceRunLoading={actionSettings.forceRunLoading}
        conditions={actionSettings.conditions}
        onConditionsChange={actionSettings.handleConditionsChange}
        popUpOpen={actionSettings.popUpOpen}
        forceRunResult={actionSettings.forceRunResult}
        onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
        tags={tags}
        primaryColor="#5757b0"
      >
        <CopyFilePathCard
          title={t('workflow.copy_file_source_title', 'Source file')}
          label={t('workflow.copy_file_source_label', 'File to copy (optional)')}
          value={sourcePath}
          onChange={setSourcePath}
          placeholder={t('workflow.copy_file_source_placeholder', 'C:\\Input\\report.pdf')}
          helperText={t(
            'workflow.copy_file_source_help',
            'Leave blank to copy the file that triggered the event.',
          )}
          browseLabel={t('workflow.copy_file_source_browse', 'Browse file')}
          onBrowse={handleBrowseSourceClick}
          browseDisabled={sourceBrowsingDisabled}
          caseid={caseid}
          tags={availableTags}
        />

        <CopyFilePathCard
          title={t('workflow.copy_file_target_title', 'Destination')}
          label={t('workflow.copy_file_target_label', 'Destination path')}
          value={targetPath}
          onChange={setTargetPath}
          placeholder={t(
            'workflow.copy_file_target_placeholder',
            'C:\\Output\\{filename}.{file_extension}',
          )}
          helperText={targetPathMissing
            ? t('workflow.copy_file_target_required', 'Destination path is required.')
            : t(
              'workflow.copy_file_target_help',
              'Choose the full output path. Dynamic tags are supported.',
            )}
          error={targetPathMissing}
          browseLabel={t('workflow.copy_file_target_browse', 'Browse folder')}
          onBrowse={handleBrowseTargetClick}
          browseDisabled={targetBrowsingDisabled}
          caseid={caseid}
          tags={availableTags}
        >
          <Box sx={{ pt: 0.5 }}>
            <FormControl>
              <FormLabel sx={{ fontSize: '0.875rem', color: 'text.primary', mb: 0.5 }}>
                {t('workflow.file_exists_behavior_label', 'If output file already exists')}
              </FormLabel>
              <RadioGroup
                row
                value={replaceIfExists ? 'replace' : 'fail'}
                onChange={(event) => setReplaceIfExists(event.target.value === 'replace')}
              >
                <FormControlLabel
                  value="fail"
                  control={<Radio size="small" />}
                  label={t('workflow.copy_file_exists_behavior_fail', 'Fail the action')}
                />
                <FormControlLabel
                  value="replace"
                  control={<Radio size="small" />}
                  label={t('workflow.file_exists_behavior_replace', 'Replace')}
                />
              </RadioGroup>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t(
                'workflow.copy_file_exists_behavior_help',
                'Choose whether an existing output fails the action or is replaced.',
              )}
            </Typography>
          </Box>
        </CopyFilePathCard>
      </ActionSettingsLayout>

      {TargetFolderBrowserComponent}
      {SourceFileBrowserComponent}

      <Dialog
        open={replaceWarningOpen}
        onClose={() => setReplaceWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('workflow.copy_file_replace_warning_title', 'Overwrite Existing Files')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t(
              'workflow.copy_file_replace_warning_body',
              'Replace-if-exists is enabled. Saving this action can overwrite existing files at the destination path.',
            )}
          </Alert>
          <DialogContentText sx={{ mt: 2 }}>
            {t(
              'workflow.copy_file_replace_warning_confirm',
              'Do you want to save with this option enabled?',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReplaceWarningOpen(false)}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button color="warning" variant="contained" onClick={handleConfirmReplaceSave}>
            {t('workflow.copy_file_replace_warning_save_anyway', 'Save Anyway')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(ActionsCopyFile);
